import React, { useEffect, useState } from "react";
import StoriesPerWeekChoice from "@/components/StoriesPerWeekChoice";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Lock, CheckCircle2, Circle, CalendarClock, PartyPopper, Play, BookOpen, Star, Trophy, ArrowRight } from "lucide-react";
import { AuthedImage } from "@/components/AuthedMedia";
import { useAuth } from "@/context/AuthContext";

// Real cover art only exists for the first 10 stories so far (see
// Landing.js's own STORY_COVERS) — everything past that falls back to its
// own storybook's first page image (same source Today.js already uses for
// a story thumbnail), and finally to the plain icon-circle treatment this
// page always had, so a story with no art yet never renders a broken image.
// Art-only crops (see Landing.js's matching STORY_COVERS_ART comment for
// how these were generated) — the card already renders its own "#N · Juz
// X" + title text, so the cover's own baked-in title band is cropped off
// here rather than showing duplicate/conflicting text at thumbnail size.
const STORY_COVERS = [
  "/covers/story-1-art.webp", "/covers/story-2-art.webp", "/covers/story-3-art.webp", "/covers/story-4-art.webp",
  "/covers/story-5-art.webp", "/covers/story-6-art.webp", "/covers/story-7-art.webp", "/covers/story-8-art.webp",
  "/covers/story-9-art.webp", "/covers/story-10-art.webp", "/covers/story-11-art.webp", "/covers/story-12-art.webp",
  "/covers/story-13-art.webp", "/covers/story-14-art.webp", "/covers/story-15-art.webp", "/covers/story-16-art.webp",
  "/covers/story-17-art.webp", "/covers/story-18-art.webp", "/covers/story-19-art.webp", "/covers/story-20-art.webp", "/covers/story-21-art.webp",
];
const CARD_TONES = ["hv-tile-teal", "hv-tile-rose", "hv-tile-gold", "hv-tile-lavender"];

/**
 * When does story `seq` open?
 *
 * The cycle runs 7 days and releases two stories: story A on day 1 and
 * story B on day 4. So story `seq` unlocks this many days after the cohort
 * start date.
 */
function unlockDayFor(seq) {
  const cycle = Math.floor((seq - 1) / 2);
  const isStoryB = seq % 2 === 0;
  return cycle * 7 + (isStoryB ? 3 : 0);
}

function unlockLabel(seq, daysSinceStart) {
  if (daysSinceStart === null) return null;
  const days = unlockDayFor(seq) - daysSinceStart;
  if (days <= 0) return null;
  if (days === 1) return 'Unlocks tomorrow';
  if (days < 7) return `Unlocks in ${days} days`;
  const weeks = Math.round(days / 7);
  return weeks === 1 ? 'Unlocks next week' : `Unlocks in about ${weeks} weeks`;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function ProgressRing({ pct }) {
  const r = 30, c = 2 * Math.PI * r, shown = Math.max(0, Math.min(100, pct));
  const label = shown < 10 ? shown.toFixed(1) : String(Math.round(shown));
  return (
    <div className="relative w-[76px] h-[76px] shrink-0" aria-label={`${label}% overall progress`}>
      <svg width="76" height="76" viewBox="0 0 76 76" className="absolute inset-0">
        <circle cx="38" cy="38" r={r} fill="none" stroke="#E5DFC8" strokeWidth="7" />
        <circle cx="38" cy="38" r={r} fill="none" stroke="#5E9A93" strokeWidth="7" strokeLinecap="round"
                strokeDasharray={c} strokeDashoffset={c - (c * shown) / 100} transform="rotate(-90 38 38)" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-hv-navy">{label}%</div>
    </div>
  );
}

// A story's title as text. Stories that haven't been written yet have an EMPTY English title ({ en: "" }); falling back to the
// object itself (as `title?.en || title` did) makes React throw "Objects are not valid as a React child" and blanks the page.
const titleText = (title, seq) => (typeof title === "string" ? title : title && title.en) || `Story ${seq}`;

export default function ProgressPage() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [stories, setStories] = useState([]);
  const [juzFilter, setJuzFilter] = useState(1);
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  // Day count from /today, so a locked row can say when it opens instead of
  // just greying out with no explanation.
  const [daysSinceStart, setDaysSinceStart] = useState(null);
  const [current, setCurrent] = useState(null);
  // Progress is account data and is never cached, so with no connection the
  // page says so and lists the stories saved on this device instead of
  // loading forever.
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    client.get("/progress/overview")
      .then(({ data }) => setData(data))
      .catch((err) => { if (!err?.response) setOffline(true); });
    client.get("/today")
      .then(({ data }) => {
        if (data.enrolled && data.position) setDaysSinceStart(data.position.days_since);
        if (data.enrolled && data.current_story) setCurrent({ story: data.current_story, type: data.position?.current_content_type, cycle: data.position?.cycle_number });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "parent") {
      client.get("/family/children").then(({ data }) => setChildren(data.children || [])).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    // Not every Juz is a flat 20 stories (Juz 1 carries Al-Fatihah as an
    // extra 21st) — 40 comfortably covers any juz size without needing to
    // know it up front.
    client.get(`/stories?juz=${juzFilter}&limit=40&slim=1`)
      .then(({ data }) => setStories(data.stories))
      .catch(() => {});
  }, [juzFilter]);

  if (!data && offline) {
    return (
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="dashboard-offline">
        <div className="hv-card p-6">
          <h1 className="hv-heading text-2xl text-hv-navy">You're offline</h1>
          <p className="mt-2 text-sm text-hv-slate">
            Your progress and this week's lessons will load when you're back online. Stories you downloaded still open.
          </p>
        </div>
        {stories.length > 0 && (
          <section className="mt-6" data-testid="dashboard-offline-stories">
            <h2 className="font-bold text-hv-navy mb-3">Available offline</h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {stories.map((s) => (
                <li key={s.global_sequence}>
                  <Link to={`/story/${s.global_sequence}`} className="hv-card block p-4 font-bold text-hv-navy hover:bg-hv-cream">
                    Story {s.global_sequence}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    );
  }

  if (!data) return <div className="p-12 text-hv-slate">Loading…</div>;
  const overallPct = data.total_stories ? (100 * data.completed_count) / data.total_stories : 0;

  // A Juz counts as finished only when ALL of its stories are done — its
  // real size (from juz_sizes), not a hardcoded 20 that Juz 1 (21) breaks.
  const finishedJuz = Object.entries(data.juz_progress || {})
    .filter(([j, n]) => n >= (data.juz_sizes?.[j] ?? 20))
    .map(([j]) => Number(j))
    .sort((a, b) => a - b);

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-10" data-testid="progress-page">
      {/* Header card and Continue card, laid out like the Poems dashboard:
          learner name and overall progress on top, then the next story. */}
      <section className="rounded-[2rem] bg-white shadow-sm p-6 sm:p-8 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between" data-testid="dashboard-hero">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-hv-teal p-2.5 text-white shrink-0">
              <BookOpen size={22} strokeWidth={2.5} />
            </span>
            <h2 className="hv-heading text-2xl sm:text-4xl text-hv-navy">
              {user?.name ? `${user.name.split(" ")[0]}'s learning` : "Your learning"}
            </h2>
          </div>
          <p className="text-hv-slate mt-2">Pick a story to continue today&apos;s lesson.</p>
          <div className="mt-4 inline-flex items-center gap-2 text-hv-navy font-bold">
            <Star size={18} className="text-hv-gold" fill="#E2C27E" strokeWidth={0} />
            {data.completed_count} {data.completed_count === 1 ? "story" : "stories"} finished so far
          </div>
        </div>
        <div className="w-full sm:w-auto rounded-3xl hv-tile-lavender px-5 py-4 flex items-center gap-4" data-testid="dashboard-overall">
          <ProgressRing pct={overallPct} />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-hv-slate font-bold">Overall progress</div>
            <div className="hv-heading text-hv-navy text-2xl">{Math.round(overallPct * 10) / 10}%</div>
            <div className="text-xs text-hv-slate">{data.completed_count} of {data.total_stories} stories</div>
          </div>
        </div>
      </section>

      <div className="mt-6">
        <StoriesPerWeekChoice />
      </div>

      {current?.story && (
        <section className="mt-6 rounded-[2rem] hv-tile-lavender p-6 sm:p-8 flex flex-wrap items-center gap-6 relative overflow-hidden" data-testid="dashboard-continue">
          <div className="flex-1 min-w-[220px]">
            <span className="inline-flex items-center justify-center rounded-2xl bg-white p-3 shadow-sm">
              <Star size={22} strokeWidth={2.2} className="text-hv-navy" />
            </span>
            <div className="hv-heading text-hv-navy text-2xl sm:text-3xl mt-4">Continue this week</div>
            <div className="text-hv-slate mt-1">{titleText(current.story.title, current.story.global_sequence)}</div>
            <div className="mt-3 flex flex-wrap gap-2 text-sm font-bold text-hv-navy">
              <span className="rounded-full bg-white px-3 py-1">Week {String(current.cycle || 1).padStart(2, "0")}</span>
              <span className="rounded-full bg-white px-3 py-1">Story {current.story.global_sequence}</span>
              <span className="rounded-full bg-hv-teal-tint px-3 py-1">
                {current.type === "video" && "Watch"}
                {current.type === "storybook" && "Storybook"}
                {current.type === "learn" && "Learn & quiz"}
                {current.type === "live" && "Recap"}
              </span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white overflow-hidden max-w-md">
              <div className="h-full rounded-full bg-hv-teal" style={{ width: `${Math.max(4, overallPct)}%` }} />
            </div>
            <div className="text-xs text-hv-slate mt-1">{Math.round(overallPct * 10) / 10}% of the full journey done</div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-3">
            {/* The ring repeats the progress bar on phones, so it shows from sm up only. */}
            <Link to={`/story/${current.story.global_sequence}`} className="hv-btn-primary inline-flex items-center gap-2" data-testid="dashboard-continue-btn">
              <Trophy size={18} strokeWidth={2.4} /> Continue <ArrowRight size={18} strokeWidth={2.6} />
            </Link>
          </div>
        </section>
      )}

      {/* Choose who is learning: parents see their linked children here, like
          the Poems dashboard's learner picker. Each child signs in with her own
          account in stories, so this shows progress rather than switching views. */}
      {children.length > 0 && (
        <section className="mt-8 hv-card p-6" data-testid="dashboard-children">
          <h2 className="hv-heading text-xl text-hv-navy">Choose who is learning</h2>
          <p className="text-hv-slate text-sm mt-1">How each child is doing.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {children.map((c) => (
              <div key={c.id} className="rounded-2xl hv-tile-teal px-4 py-3 min-w-[180px]" data-testid={`child-chip-${c.id}`}>
                <div className="hv-heading text-hv-navy">{c.name || c.email}</div>
                <div className="text-xs text-hv-slate mt-0.5">{c.completed} of {c.unlocked} stories done · {c.completion_pct}%</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Overview stats */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        <div className="hv-card p-5" data-testid="progress-completed">
          <div className="text-4xl font-heading font-bold text-hv-teal">{data.completed_count}</div>
          <div className="text-xs uppercase tracking-wider text-hv-slate mt-1 font-bold">{t("stories_done")}</div>
        </div>
        <div className="hv-card p-5" data-testid="progress-unlocked">
          <div className="text-4xl font-heading font-bold text-hv-teal">{data.max_unlocked}</div>
          <div className="text-xs uppercase tracking-wider text-hv-slate mt-1 font-bold">Unlocked</div>
        </div>
        <div className="hv-card p-5" data-testid="progress-total">
          <div className="text-4xl font-heading font-bold text-hv-teal">{data.total_stories}</div>
          <div className="text-xs uppercase tracking-wider text-hv-slate mt-1 font-bold">Total</div>
        </div>
      </div>

      {/* The one moment worth animating: a finished Juz. */}
      {finishedJuz.length > 0 && (
        <div className="mt-8 hv-card p-7 relative overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-700"
             data-testid="juz-celebration">
          <div className="flex items-center gap-4">
            <div className="hv-icon-circle-gold w-14 h-14 rounded-full flex items-center justify-center shrink-0 animate-bounce">
              <PartyPopper size={26} strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-hv-teal font-bold">Mabrook!</div>
              <div className="hv-heading text-hv-navy text-2xl mt-1">
                {finishedJuz.length === 1
                  ? `You finished Juz ${finishedJuz[0]}.`
                  : `You have finished ${finishedJuz.length} Juz.`}
              </div>
              <p className="text-hv-slate text-sm mt-1">
                {finishedJuz.length === 1
                  ? `All ${data.juz_sizes?.[finishedJuz[0]] ?? 20} stories complete. That is a whole Juz of the Qur'an.`
                  : "Every story in each done. That is a whole Juz of the Qur'an, every time."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Juz map */}
      <div className="mt-10">
        <h2 className="hv-heading text-2xl sm:text-3xl text-hv-navy flex items-center gap-2"><BookOpen size={26} className="text-hv-teal" strokeWidth={2.5} /> All 600 stories</h2>
        <p className="text-hv-slate text-sm mt-1">Pick a juz, then open any story you have reached.</p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="juz-selector">
          {(() => {
            // Each juz starts after the stories of every juz before it, so a juz is
            // reachable once the learner's unlocked story reaches its first story.
            let nextStart = 1;
            return Array.from({ length: 30 }, (_, i) => i + 1).map((j) => {
              const startSeq = nextStart;
              nextStart += data.juz_sizes?.[j] ?? 20;
              const reachable = j <= 3 || startSeq <= (data.max_unlocked || 0);
              const done = (data.juz_progress?.[j] || 0);
              const isActive = juzFilter === j;
              const style = isActive ? "hv-dot-active" : !reachable ? "opacity-40 cursor-not-allowed" : (done > 0 ? "hv-dot-done" : "hv-dot-upcoming");
              return (
                <button key={j} onClick={() => reachable && setJuzFilter(j)} disabled={!reachable}
                        data-testid={`juz-btn-${j}`}
                        title={reachable ? `Juz ${j}` : `Unlocks at story ${startSeq}`}
                        className={`hv-dot ${style}`}>
                  {j}
                </button>
              );
            });
          })()}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          {stories.map((s) => {
            const state = s.completed ? "done" : s.unlocked ? "unlocked" : "locked";
            const soon = state === "locked" ? unlockLabel(s.global_sequence, daysSinceStart) : null;
            const cover = STORY_COVERS[s.global_sequence - 1] || s.storybook?.[0]?.image_url || null;
            const tone = CARD_TONES[(s.global_sequence - 1) % CARD_TONES.length];
            const Wrapper = state === "locked" ? "div" : Link;
            const wrapperProps = state === "locked" ? {} : { to: `/story/${s.global_sequence}` };
            return (
              <Wrapper
                key={s.global_sequence}
                {...wrapperProps}
                data-testid={`story-row-${s.global_sequence}`}
                className={`relative flex items-center gap-4 rounded-[1.75rem] p-3 pe-5 shadow-sm transition-all ${tone} ${
                  state === "locked" ? "opacity-60" : state === "done" ? "opacity-95 hover:shadow-md hover:-translate-y-0.5" : "hover:shadow-md hover:-translate-y-0.5"}`}
              >
                {cover ? (
                  <div className="w-[160px] h-[125px] sm:w-[200px] sm:h-[155px] rounded-2xl overflow-hidden shrink-0 bg-hv-navy">
                    <AuthedImage src={cover} alt={titleText(s.title, s.global_sequence)} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-[160px] h-[125px] sm:w-[200px] sm:h-[155px] rounded-2xl shrink-0 bg-white/40 flex items-center justify-center">
                    <div className={`hv-icon-circle w-14 h-14 sm:w-16 sm:h-16 ${
                      state === "done" ? "hv-icon-circle-gold" : state === "unlocked" ? "hv-icon-circle-coral" : ""}`}>
                      {state === "done" ? <CheckCircle2 size={26} strokeWidth={2.5} /> :
                       state === "locked" ? <Lock size={22} strokeWidth={2.5} /> :
                       <Circle size={26} strokeWidth={2.5} />}
                    </div>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-hv-teal">#{s.global_sequence} · Juz {s.juz_number}</div>
                  <div className="hv-heading text-hv-navy text-base sm:text-lg leading-snug mt-1 line-clamp-2">
                    {titleText(s.title, s.global_sequence)}
                  </div>
                  {state === "locked" && soon ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-hv-slate"
                         data-testid={`story-locked-when-${s.global_sequence}`}>
                      <CalendarClock size={14} strokeWidth={2.5} className="text-hv-teal" />
                      {soon}
                    </div>
                  ) : state === "done" ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-hv-teal"
                         data-testid={`open-story-${s.global_sequence}`}>
                      <Play size={12} fill="currentColor" strokeWidth={0} /> Watch again
                    </div>
                  ) : null}
                </div>
                <span className={`absolute top-3 end-3 w-7 h-7 rounded-full shadow flex items-center justify-center text-xs font-bold shrink-0 ${
                  state === "done" ? "bg-[#3BA55C] text-white" : "bg-white text-hv-navy"}`}>
                  {state === "done" ? <CheckCircle2 size={14} strokeWidth={3} /> :
                   state === "locked" ? <Lock size={12} strokeWidth={3} /> :
                   s.global_sequence}
                </span>
              </Wrapper>
            );
          })}
          {stories.length === 0 && (
            <div className="hv-card p-6 text-hv-slate">
              No stories loaded for Juz {juzFilter} yet. Admin can bulk-import via the CMS.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
